import boxHeaderIcon from "@assets/images/box.svg";
import trashTeal from "@assets/images/trash.svg";
import { Button, Grid, Paper, Typography } from "@material-ui/core";
import Checkbox from "@saleor/components/Checkbox";
import DatePicker from "@saleor/components/DatePicker";
import DecimalFormat from "@saleor/components/DecimalFormat";
import DialogConfirmDelete from "@saleor/components/DialogConfirmDelete";
import MyTypography from "@saleor/components/MyTypography";
import NumberFormat from "@saleor/components/NumberFormat";
import TablePaginationCustom from "@saleor/components/TablePaginationCustom";
import { ProductListPaginationProps } from "@saleor/createOrderCustomer/components/ProductListPagination";
import { ValidateFormProps } from "@saleor/createOrderCustomer/views/CreateOrderView";
import useBulkActions from "@saleor/hooks/useBulkActions";
import { convertToRolExport } from "@saleor/utils/data/convertUnit";
import { getPreviousDate, parseDateString } from "@saleor/utils/date";
import classNames from "classnames";
import { useFormik } from "formik";
import moment from "moment";
import React, { memo, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import * as Yup from "yup";

import useStyles from "./styles";

interface ProductDetailsProps extends ProductListPaginationProps {
  listLineAll: any[];
  listLineCurrent: any[];
  setListLine?: (value) => void;
  validateForm: ValidateFormProps;
  setValidateForm: (values: ValidateFormProps) => void;
  deleteOrderLine: (list: string[]) => void;
  disabled: boolean;
  componentKey?: number;
  currency: string;
}

interface deleteSingleLine {
  id: string[];
  title: string;
  content: string | React.ReactNode;
}

const ProductDetails: React.FC<ProductDetailsProps> = memo(props => {
  const {
    listLineAll,
    listLineCurrent,
    setListLine,
    validateForm,
    setValidateForm,
    onNextPage,
    onPreviousPage,
    hasPreviousPage,
    hasNextPage,
    onUpdateListSettings,
    settings,
    deleteOrderLine,
    disabled,
    currency
  } = props;
  const classes = useStyles();
  const intl = useIntl();
  const {
    isSelected,
    listElements,
    reset,
    set,
    toggle,
    toggleAll
  } = useBulkActions();
  const [singleSelectId, setSingleSelectId] = useState(null);
  const [openConfrmDelete, setOpenConfirmDelete] = useState<boolean>(false);
  const [dataDelete, setDataDelete] = useState<deleteSingleLine>({
    content: "",
    id: [],
    title: ""
  });

  const SignupSchema = Yup.object().shape({
    quantity: Yup.array().of(Yup.number().required("Required")),
    requestDeliveryDate: Yup.array().of(
      Yup.date()
        .transform(parseDateString)
        .min(
          getPreviousDate(),
          "กรุณาเปลี่ยน “Request Date” (ไม่สามารถใส่วันที่ผ่านมาแล้วได้)"
        )
        .required("Required")
    )
  });

  const formik = useFormik({
    initialValues: {
      quantity: listLineCurrent.map(line => line.quantity),
      requestDeliveryDate: listLineCurrent.map(line => line.requestDeliveryDate)
    },
    onSubmit: () => undefined,
    validationSchema: SignupSchema,
    validateOnMount: false
  });

  useEffect(() => {
    if (validateForm?.isCheckValidate && !formik.isValid) {
      // Verify validate form
      formik.handleSubmit();
    }

    // Validate submit
    setValidateForm({
      ...validateForm,
      isValidate: {
        ...validateForm.isValidate,
        validateProduct: formik.isValid
      }
    });
  }, [validateForm?.isCheckValidate, formik.isValid]);

  useEffect(() => {
    formik.validateForm();
  }, []);

  const onDeleteSingle = id => {
    reset();
    setSingleSelectId(id);
    set([id]);
    setOpenConfirmDelete(true);
    setDataDelete({
      id: [id],
      title: "ลบรายการนี้",
      content: "คุณกำลังจะลบรายการสินค้านี้"
    });
  };

  const onDeleteSelect = () => {
    setOpenConfirmDelete(true);
    setDataDelete({
      id: listElements,
      title: "ลบรายการสินค้าที่เลือก",
      content: (
        <span>
          คุณกำลังจะลบสินค้า <b>{listElements.length}</b> รายการ ในคำสั่งซื้อนี้
        </span>
      )
    });
  };

  const confirmDelete = () => {
    setOpenConfirmDelete(false);
    deleteOrderLine(dataDelete.id);
    reset();
  };

  const getTotalQuantityGroupByVariant = line => {
    let total = 0;
    listLineAll.forEach(item => {
      if (item.contractProduct?.id === line.contractProduct?.id) {
        total += Number(item.weightPerUnit) * (item.quantity || 0);
      }
    });
    return total;
  };
  const onChangeQuantity = (event, lineId) => {
    formik.handleChange(event);
    const value = parseInt(event.target.value, 10);
    const linesAll = [...listLineAll];
    const lineChange = linesAll.find(line => line.id === lineId);
    const remaining = lineChange.contractProduct?.remainingQuantity || 0;

    lineChange.quantity = value || 0;
    lineChange.errorQuantity =
      !value || getTotalQuantityGroupByVariant(lineChange) > remaining;

    linesAll.forEach(line => {
      if (
        lineChange.contractProduct?.id === line.contractProduct?.id &&
        line.id !== lineId
      ) {
        if (!lineChange.errorQuantity && line.quantity) {
          line.errorQuantity = false;
        }
      }
    });

    setListLine(linesAll);
  };

  const onChangeRequestDate = (event, lineId) => {
    formik.handleChange(event);
    const linesAll = [...listLineAll];
    const lineChange = linesAll.find(line => line.id === lineId);
    lineChange.requestDeliveryDate = event.target.value;
    lineChange.errorDate = moment(event.target.value)
      .set("h", 1)
      .isBefore(moment().set("h", 0));
    setListLine(linesAll);
  };

  const handleToggleCheckboxAll = () => {
    if (listElements.length === listLineCurrent.length) {
      reset();
      return;
    }
    toggleAll(listLineCurrent, listElements.length);
  };

  const renderTextErrorQuantity = line => {
    if (line.quantity) {
      return intl.formatMessage({
        defaultMessage: "จำนวนที่สั่งมีมากกว่าจำนวนคงเหลือ"
      });
    } else {
      return intl.formatMessage({ defaultMessage: "กรุณาระบุจำนวน" });
    }
  };

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <img alt="box-header-icon" src={boxHeaderIcon} />
        <Typography variant="h6" className={classes.title}>
          {intl.formatMessage({
            defaultMessage: "รายละเอียดสินค้า"
          })}
        </Typography>
      </div>
      <div className={classes.subHeader}>
        <MyTypography variant="subtitle2" className={classes.subTitle}>
          <span className={classes.normalText}>มีทั้งหมด</span>{" "}
          {listLineAll.length}{" "}
          <span className={classes.normalText}>รายการ</span>
        </MyTypography>
        <div className={classes.selectDivHeader}>
          <div className={classes.selectDivLeft}>
            <Checkbox
              id="productDetails-checkboxAll"
              size={"small"}
              onClick={handleToggleCheckboxAll}
              checked={listLineCurrent.length === listElements.length}
              indeterminate={
                listElements.length > 0 &&
                listLineCurrent.length > listElements.length
              }
            />
            <MyTypography
              variant="subtitle3"
              className={classNames(
                listElements.length === 0
                  ? classes.subTitleDisable
                  : classes.subTitle
              )}
            >
              <span className={classes.normalText}>เลือกลบ</span>{" "}
              {listElements.length}{" "}
              <span className={classes.normalText}>รายการ</span>
            </MyTypography>
          </div>
          <Button
            variant={"outlined"}
            color={"primary"}
            onClick={onDeleteSelect}
            disabled={listElements.length === 0 || disabled}
          >
            <MyTypography
              variant="body3"
              className={classNames(
                listElements.length === 0 && classes.txtDisable
              )}
            >
              ลบรายการสินค้าที่เลือก
            </MyTypography>
          </Button>
        </div>
      </div>
      {listLineCurrent?.map((line, index) => (
        <Paper className={classes.itemContainer} key={line.id}>
          <Checkbox
            id={`productDetails[${index}].checkbox`}
            size={"small"}
            onClick={() => toggle(line.id)}
            checked={isSelected(line.id)}
          />
          <div className={classes.itemWrapper}>
            <Grid container>
              <Grid item lg={12}>
                <Grid item lg={12}>
                  <MyTypography
                    variant="subtitle2"
                    className={classes.itemTitle}
                  >
                    {line?.variant?.descriptionEn}
                  </MyTypography>
                </Grid>
                <div className={classes.gridContainer}>
                  <div className={classes.item1}>
                    <MyTypography
                      variant="caption"
                      className={classes.itemLabel}
                    >
                      {intl.formatMessage({
                        defaultMessage: "จำนวน",
                        description: "quantity"
                      })}
                      <span className={classes.required}>*</span>
                    </MyTypography>
                    <NumberFormat
                      id={`productDetails[${index}].quantity`}
                      customClass={classes.inputQuantity}
                      error={
                        !!(
                          formik.errors.quantity &&
                          formik.errors.quantity[index] &&
                          formik.touched.quantity &&
                          formik.touched.quantity[index]
                        ) || line.errorQuantity
                      }
                      name={`quantity[${index}]`}
                      value={formik.values.quantity[index]}
                      onChange={event => onChangeQuantity(event, line.id)}
                      allowLeadingZeros
                      decimalScale={0}
                    />
                    <div className={classes.errorText}>
                      {!!(
                        formik.errors.quantity &&
                        formik.errors.quantity[index] &&
                        formik.touched.quantity &&
                        formik.touched.quantity[index]
                      ) || line.errorQuantity
                        ? renderTextErrorQuantity(line)
                        : ""}
                    </div>
                  </div>
                  <div className={classes.item2}>
                    <MyTypography
                      variant="caption"
                      className={classes.itemLabel}
                    >
                      {intl.formatMessage({
                        defaultMessage: "หน่วย",
                        description: "sale unit"
                      })}
                    </MyTypography>
                    <MyTypography variant="body3" className={classes.spanLine}>
                      {convertToRolExport(null)}
                    </MyTypography>
                  </div>
                  <div className={classes.item3}>
                    <MyTypography
                      variant="caption"
                      className={classes.itemLabel}
                    >
                      น้ำหนัก <span className={classes.normalText}>(ตัน)</span>{" "}
                      / หน่วย
                    </MyTypography>
                    <MyTypography variant="body3" className={classes.spanLine}>
                      <DecimalFormat
                        value={line?.weightPerUnit}
                        isGrayDecimal={false}
                        countDecimal={3}
                      />
                    </MyTypography>
                  </div>
                  <div className={classes.item4}>
                    <MyTypography
                      variant="caption"
                      className={classes.itemLabel}
                    >
                      น้ำหนักรวม{" "}
                      <span className={classes.normalText}>(ตัน)</span>
                    </MyTypography>
                    <MyTypography variant="body3" className={classes.spanLine}>
                      <DecimalFormat
                        value={line?.weightPerUnit * line?.quantity}
                        isGrayDecimal={false}
                        countDecimal={3}
                      />
                    </MyTypography>
                  </div>
                  <div className={classes.item5}>
                    <MyTypography
                      variant="caption"
                      className={classes.itemLabel}
                    >
                      ราคา{" "}
                      <span className={classes.normalText}>({currency})</span> /
                      หน่วย
                    </MyTypography>
                    <MyTypography variant="body3" className={classes.spanLine}>
                      <DecimalFormat
                        value={line?.contractProduct?.pricePerUnit}
                        isGrayDecimal={false}
                        countDecimal={2}
                      />
                    </MyTypography>
                  </div>
                  <div className={classes.item6}>
                    <MyTypography
                      variant="caption"
                      className={classes.itemLabel}
                    >
                      ราคารวมสุทธิ{" "}
                      <span className={classes.normalText}>({currency})</span>
                    </MyTypography>
                    <MyTypography variant="body3" className={classes.spanLine}>
                      {line?.contractProduct?.pricePerUnit &&
                      formik.values.quantity[index] ? (
                        <DecimalFormat
                          value={
                            line?.contractProduct?.pricePerUnit *
                            line?.weightPerUnit *
                            formik.values.quantity[index]
                          }
                          isGrayDecimal={false}
                          countDecimal={2}
                        />
                      ) : (
                        "0.00"
                      )}
                    </MyTypography>
                  </div>
                  <div className={classes.item7}>
                    <MyTypography
                      variant="caption"
                      className={classes.itemLabel}
                    >
                      เงื่อนไขการชำระเงิน
                    </MyTypography>
                    <MyTypography variant="body3" className={classes.spanLine}>
                      {
                        line?.contractProduct?.contract
                          ?.paymentTermDescriptionTh
                      }
                    </MyTypography>
                  </div>
                  <div className={classes.item8}>
                    <MyTypography
                      variant="caption"
                      className={classes.itemLabel}
                    >
                      {intl.formatMessage({
                        defaultMessage: "วันที่ต้องการสินค้า"
                      })}
                      <span className={classes.required}>*</span>
                    </MyTypography>
                    <DatePicker
                      error={
                        (formik.errors.requestDeliveryDate &&
                          formik.errors.requestDeliveryDate[index] &&
                          formik.touched.requestDeliveryDate &&
                          formik.touched.requestDeliveryDate[index]) ||
                        line.errorDate
                      }
                      name={`requestDeliveryDate[${index}]`}
                      id={`productDetails[${index}].requestDeliveryDate`}
                      value={formik.values.requestDeliveryDate[index] || ""}
                      onChange={event => onChangeRequestDate(event, line.id)}
                    />
                    <div className={classes.errorText}>
                      {formik.errors.requestDeliveryDate &&
                      formik.touched.requestDeliveryDate &&
                      formik.errors.requestDeliveryDate[index] ===
                        "กรุณาเปลี่ยน “Request Date” (ไม่สามารถใส่วันที่ผ่านมาแล้วได้)"
                        ? formik.errors.requestDeliveryDate[index]
                        : null}
                    </div>
                  </div>
                </div>
              </Grid>
            </Grid>
            <Button
              id={`productDetails[${index}].deleteButton`}
              data-testid={`productDetail.deleteButton`}
              className={classes.btnDelete}
              disabled={disabled}
              onClick={() => onDeleteSingle(line.id)}
            >
              <img src={trashTeal} alt={`trash-teal-button-${index}`} />
            </Button>
          </div>
        </Paper>
      ))}
      <TablePaginationCustom
        settings={settings}
        hasNextPage={hasNextPage}
        onNextPage={onNextPage}
        onUpdateListSettings={onUpdateListSettings}
        hasPreviousPage={hasPreviousPage}
        onPreviousPage={onPreviousPage}
        totalCount={listLineAll.length}
        selectColor="#007E92"
        disabled={disabled}
        noOfRowsThai
      />
      <DialogConfirmDelete
        onCancel={() => {
          setOpenConfirmDelete(false);
          toggle(singleSelectId);
        }}
        onConfirm={confirmDelete}
        open={openConfrmDelete}
        title={dataDelete.title}
        content={dataDelete.content}
      />
    </div>
  );
});

export default ProductDetails;
